import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { canAccess, MODULES } from '../../../auth/permissions';
import { logError } from '../../../utils/logger';
import { useAuth } from '../../auth/auth.context';
import { ModalWrapper, SectionTitle } from '../../shared/components';
import { JOB_MASTERS, JobMasterKey } from './masters.config';
import MasterSelectField from './MasterSelectField';
import { MasterRow } from './MasterFormDialog';
import { useAddedMasterRows } from './useAddedMasterRows';
import {
  financialYearForDate,
  FilterOption,
  updateWorkJob,
  updateWorkJobStatus,
  WorkJobReportFilterOptions,
  WorkJobReportRow,
  WorkJobUpdatePayload,
} from './workJobReport.api';

/** The status label the API special-cases to also store a cancellation reason. */
const CANCELED_LABEL = 'Canceled';

interface FormState {
  company_id: number | null;
  job_code: string;
  job_name: string;
  job_date: string;
  locality_id: number | null;
  site_address: string;
  project_type_id: number | null;
  construction_work_type_id: number | null;
  building_type_id: number | null;
  status_id: number | null;
  client_id: number | null;
  architect_id: number | null;
  builder_id: number | null;
  owner_id: number | null;
  cancellation_reason: string;
}

const toForm = (row: WorkJobReportRow): FormState => ({
  company_id: row.company_id ?? null,
  job_code: row.job_code ?? '',
  job_name: row.job_name ?? '',
  job_date: row.job_date ?? '',
  locality_id: row.locality_id,
  site_address: row.site_address ?? '',
  project_type_id: row.project_type_id,
  construction_work_type_id: row.construction_work_type_id,
  building_type_id: row.building_type_id,
  status_id: row.status_id,
  client_id: row.client_id,
  architect_id: row.architect_id,
  builder_id: row.builder_id,
  owner_id: row.owner_id,
  cancellation_reason: '',
});

/**
 * `job_code`, `job_name`, `job_date` and `site_address` are all NOT NULL on `work_jobs`,
 * and the update endpoint assigns them straight through. Blanking one here would fail at
 * the database with an opaque message, so they are required.
 *
 * Locality is checked separately — since V12 it is a picker over the `localities` master,
 * so what has to be non-empty is the selected id, not a string.
 */
const REQUIRED_TEXT: Array<{ key: keyof FormState; label: string }> = [
  { key: 'job_code', label: 'Job Code' },
  { key: 'job_name', label: 'Job Name' },
  { key: 'job_date', label: 'Job Date' },
  { key: 'site_address', label: 'Site Address' },
];

interface WorkJobEditModalProps {
  /** The row being edited; `null` keeps the modal closed. */
  row: WorkJobReportRow | null;
  options: WorkJobReportFilterOptions;
  onClose: () => void;
  /** Called after a successful save so the list can refetch. */
  onSaved: () => void;
  /** Called when a master row was added or edited, so the option lists reload. */
  onMastersChanged?: () => void;
}

/**
 * Edits one work job from the report, in place.
 *
 * Only the fields the report itself shows are editable — the quote, floors, milestones
 * and files belong to the full project form. That is safe because
 * `PUT /api/analyst/job/{id}/update` touches only the keys present in the body, so
 * everything this dialog leaves out is left exactly as it was.
 *
 * The Type of Work and Quotation panels used to hang off this dialog; they live behind the
 * row's Job Actions button now, alongside Assign Work.
 */
export default function WorkJobEditModal({
  row,
  options,
  onClose,
  onSaved,
  onMastersChanged,
}: WorkJobEditModalProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Rows added from inside this dialog — see useAddedMasterRows for why the form owns them.
  const { withAdded, remember: rememberMasterRow, clear: clearAddedRows } = useAddedMasterRows();

  // Reset to the row's values every time a different row is opened.
  useEffect(() => {
    setForm(row ? toForm(row) : null);
    clearAddedRows();
    setError(null);
  }, [row, clearAddedRows]);

  const statusLabel = useMemo(
    () => options.statuses.find((s) => s.id === form?.status_id)?.label ?? null,
    [options.statuses, form?.status_id]
  );

  /**
   * Derived from the job date, never chosen: the financial year is a fact about the date
   * (April to March), so it is shown rather than edited. The save re-derives it server-side
   * from the same date, which is what actually persists it — this is the preview of that.
   */
  const financialYear = useMemo(
    () => financialYearForDate(form?.job_date ?? '', options.financial_years),
    [form?.job_date, options.financial_years]
  );

  const financialYearChanged =
    Boolean(financialYear) && financialYear!.label !== (row?.financial_year ?? null);

  const isCanceling = statusLabel === CANCELED_LABEL && row?.status !== CANCELED_LABEL;

  /** Only the admin may add or edit a company, so only the admin gets "+ Add" and the pencil. */
  const { role } = useAuth();
  const canEditCompanies = canAccess(role, MODULES.COMPANY_EDIT);

  /**
   * The active companies, plus the job's own when it has since been made inactive — the
   * picker would otherwise show the job as having no company at all.
   */
  const companyOptions = useMemo(() => {
    const list = options.companies ?? [];
    if (!row?.company_id || list.some((c) => c.id === row.company_id)) return list;
    return [...list, { id: row.company_id, label: `${row.company_name ?? 'Company'} (inactive)` }];
  }, [options.companies, row?.company_id, row?.company_name]);

  if (!row || !form) return null;

  const setField = (key: keyof FormState, value: string | number | null) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  /** The label for a selected id — the update endpoint takes labels, not ids. */
  const labelFor = (list: FilterOption[], id: number | null) =>
    list.find((option) => option.id === id)?.label;

  /**
   * Only changed keys go into the payload. That matters for the three lookups:
   * `resolveProjectType` and `resolveConstructionWorkType` fall back to a default
   * ("New Job" / "Allied Work") rather than null when a value does not match, so
   * sending one needlessly could silently rewrite the job's type.
   */
  const buildPayload = (): WorkJobUpdatePayload => {
    const payload: WorkJobUpdatePayload = {};

    if (form.job_code.trim() !== (row.job_code ?? '')) payload.job_code = form.job_code.trim();
    if (form.job_name.trim() !== (row.job_name ?? '')) payload.job_name = form.job_name.trim();
    // The server derives the financial year from `job_date`, so the date also goes along
    // when the derived year disagrees with what is stored — that is what repairs a job
    // saved before the year was derived at all.
    if (form.job_date && (form.job_date !== (row.job_date ?? '') || financialYearChanged))
      payload.job_date = form.job_date;
    if (form.locality_id !== row.locality_id) {
      const label = labelFor(withAdded(options.localities, 'localities'), form.locality_id);
      if (label) payload.locality = label;
    }
    if (form.site_address.trim() !== (row.site_address ?? ''))
      payload.site_address = form.site_address.trim();

    if (form.project_type_id !== row.project_type_id) {
      const label = labelFor(withAdded(options.project_types, 'projectTypes'), form.project_type_id);
      if (label) payload.job_type = label;
    }
    if (form.construction_work_type_id !== row.construction_work_type_id) {
      const label = labelFor(withAdded(options.construction_work_types, 'constructionWorkTypes'), form.construction_work_type_id);
      if (label) payload.job_work_type = label;
    }
    if (form.building_type_id !== row.building_type_id) {
      const label = labelFor(withAdded(options.building_types, 'buildingTypes'), form.building_type_id);
      if (label) payload.building_type = label;
    }

    if (form.company_id !== (row.company_id ?? null) && form.company_id)
      payload.company_id = form.company_id;
    if (form.client_id !== row.client_id && form.client_id) payload.client_id = form.client_id;
    if (form.architect_id !== row.architect_id && form.architect_id)
      payload.architect_id = form.architect_id;
    if (form.builder_id !== row.builder_id && form.builder_id) payload.builder_id = form.builder_id;
    if (form.owner_id !== row.owner_id && form.owner_id) payload.owner_id = form.owner_id;

    return payload;
  };

  const handleSave = async () => {
    const missing = REQUIRED_TEXT.filter(({ key }) => !String(form[key] ?? '').trim());
    if (!form.locality_id) missing.push({ key: 'locality_id', label: 'Locality' });
    if (missing.length > 0) {
      setError(`Required: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    if (isCanceling && !form.cancellation_reason.trim()) {
      setError('A cancellation reason is required when moving a job to Canceled.');
      return;
    }

    const payload = buildPayload();
    const statusChanged = form.status_id !== row.status_id && Boolean(statusLabel);

    if (Object.keys(payload).length === 0 && !statusChanged) {
      setError('Nothing has changed.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (Object.keys(payload).length > 0) {
        await updateWorkJob(row.id, payload);
      }
      // Status has its own endpoint; `updateJob` has no status branch at all.
      if (statusChanged && statusLabel) {
        await updateWorkJobStatus(
          row.id,
          statusLabel,
          isCanceling ? form.cancellation_reason.trim() : undefined
        );
      }
      toast.success(`${row.job_code} updated`);
      onSaved();
      onClose();
    } catch (err) {
      setError(
        logError({
          message: 'Failed to update job',
          error: err,
          code: 'WORK_JOB_UPDATE_FAILED',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * One picker over a master list. `masterKey` is what gives the field its "+ Add" button
   * and the pencil on each row; Status is passed none, so it stays a plain picker — status
   * rows carry a category and drive workflow logic, and one invented here would be accepted
   * by the form and then ignored by the code behind it.
   */
  const handleMasterSaved = (masterKey: JobMasterKey, saved: MasterRow) => {
    rememberMasterRow(masterKey, saved);
    // Let the page reload its lists too, so the report's own dropdowns catch up.
    onMastersChanged?.();
  };

  const lookupField = (
    label: string,
    key: keyof FormState,
    list: FilterOption[],
    masterKey?: JobMasterKey,
    required = false
  ) => (
    <MasterSelectField
      label={label}
      value={form[key] as number | null}
      options={withAdded(list, masterKey)}
      onChange={(id) => setField(key, id)}
      master={masterKey ? JOB_MASTERS[masterKey] : undefined}
      required={required}
      onMasterSaved={masterKey ? (saved) => handleMasterSaved(masterKey, saved) : undefined}
    />
  );

  return (
    <ModalWrapper open={Boolean(row)} onClose={saving ? () => {} : onClose} maxWidth='lg'>
      <SectionTitle
        title={`EDIT JOB · ${row.job_code} · ${row.job_name}`}
        meta={
          <Typography variant='body2' color='text.secondary' whiteSpace='nowrap'>
            {financialYear?.label ?? row.financial_year ?? '—'}
          </Typography>
        }
      />

      {error && <Alert severity='error'>{error}</Alert>}

      {/*
        Three columns at full width, collapsing to two and then one as the viewport
        narrows. At `md` the thirteen fields wrapped into a tall column that scrolled
        inside the dialog; at `lg` they all sit on screen at once.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          columnGap: 2.5,
          rowGap: 2.5,
        }}
      >
        {lookupField('Company', 'company_id', companyOptions, canEditCompanies ? 'companies' : undefined)}

        <TextField
          size='small'
          label='Job Code'
          required
          value={form.job_code}
          onChange={(e) => setField('job_code', e.target.value)}
        />
        <TextField
          size='small'
          label='Job Name'
          required
          value={form.job_name}
          onChange={(e) => setField('job_name', e.target.value)}
        />
        <TextField
          size='small'
          label='Job Date'
          type='date'
          required
          value={form.job_date}
          onChange={(e) => setField('job_date', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size='small'
          label='Financial Year'
          value={financialYear?.label ?? '—'}
          disabled
          helperText={
            financialYearChanged
              ? `Set from the job date on save (was ${row.financial_year ?? 'none'})`
              : 'Set from the job date — April to March'
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {lookupField('Locality', 'locality_id', options.localities, 'localities', true)}

        {lookupField('Project Type', 'project_type_id', options.project_types, 'projectTypes')}
        {lookupField(
          'Construction Work Type',
          'construction_work_type_id',
          options.construction_work_types,
          'constructionWorkTypes'
        )}
        {lookupField('Building Type', 'building_type_id', options.building_types, 'buildingTypes')}
        {lookupField('Status', 'status_id', options.statuses)}

        {lookupField('Client', 'client_id', options.clients, 'clients')}
        {lookupField('Architect', 'architect_id', options.architects, 'architects')}
        {lookupField('Builder', 'builder_id', options.builders, 'builders')}
        {lookupField('Owner', 'owner_id', options.owners, 'owners')}
      </Box>

      <TextField
        size='small'
        label='Site Address'
        required
        multiline
        minRows={2}
        fullWidth
        value={form.site_address}
        onChange={(e) => setField('site_address', e.target.value)}
      />

      {isCanceling && (
        <TextField
          size='small'
          label='Cancellation Reason'
          required
          multiline
          minRows={2}
          fullWidth
          value={form.cancellation_reason}
          onChange={(e) => setField('cancellation_reason', e.target.value)}
          helperText='Stored against the job when the status moves to Canceled.'
        />
      )}

      <Stack direction='row' gap={1.5} justifyContent='flex-end'>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Stack>
    </ModalWrapper>
  );
}
